'use client'

import type { ButtonBlockProps } from '@/blocks/ButtonBlock'
import { ButtonBlock } from '@/blocks/ButtonBlock'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import type { ExpectedBlockType, SbBaseBlockProps, StoryblokAsset } from '@/services/storyblok/storyblok'
import { filterByBlockType } from '@/services/storyblok/Storyblok.helpers'
import { type SbBlokData, StoryblokComponent, storyblokEditable } from '@storyblok/react'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import {
  heightLandscapeVar,
  heightPortraitVar,
  heroContent,
  heroContentAlignmentVar,
  heroImage,
  root,
} from './HeroBlock.css'

type VerticalAlignment = 'top' | 'center' | 'bottom'

type HeroBlockProps = SbBaseBlockProps<{
  background?: StoryblokAsset
  backgroundLandscape?: StoryblokAsset
  buttons: ExpectedBlockType<ButtonBlockProps>
  content: Array<SbBlokData>
  heightPortrait?: string
  heightLandscape?: string
  verticalAlignment?: VerticalAlignment
  priority?: boolean
}>

export function HeroBlock({ blok }: HeroBlockProps) {
  const buttonBlocks = filterByBlockType(blok.buttons, ButtonBlock.blockName)
  const background = blok.background?.filename ? blok.background : undefined
  const backgroundLandscape = blok.backgroundLandscape?.filename ? blok.backgroundLandscape : undefined

  return (
    <section
      className={root}
      style={assignInlineVars({
        [heightPortraitVar]: blok.heightPortrait ? `${blok.heightPortrait}vh` : null,
        [heightLandscapeVar]: blok.heightLandscape ? `${blok.heightLandscape}vh` : null,
      })}
      {...storyblokEditable(blok)}
    >
      <div style={{ zIndex: -1 }}>
        {background && (
          <ImageWithPlaceholder
            className={heroImage}
            imagePriority={blok.priority}
            src={background.filename}
            alt={background.alt}
            data-portrait-image={!!backgroundLandscape}
            fill
          />
        )}
        {backgroundLandscape && (
          <ImageWithPlaceholder
            className={heroImage}
            imagePriority={blok.priority}
            src={backgroundLandscape.filename}
            alt={backgroundLandscape.alt}
            data--image={!!background}
            fill
          />
        )}
      </div>

      <div
        className={heroContent}
        style={assignInlineVars({
          [heroContentAlignmentVar]: getAlignmentStyles(blok.verticalAlignment),
        })}
      >
        <div>
          {blok.content.map((nestedBlock) => (
            <StoryblokComponent blok={nestedBlock} key={nestedBlock._uid} />
          ))}
        </div>
        <div>
          {buttonBlocks.map((nestedBlock) => (
            <ButtonBlock blok={nestedBlock} key={nestedBlock._uid} />
          ))}
        </div>
      </div>
    </section>
  )
}

function getAlignmentStyles(alignment: VerticalAlignment | undefined) {
  switch (alignment) {
    case 'top':
      return 'flex-start'
    case 'center':
      return 'center'
    case 'bottom':
      return 'flex-end'
    default:
      return 'initial'
  }
}
